import React, { useCallback, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line,
} from "recharts";
import {
  Wallet, TrendingUp, TrendingDown, PiggyBank, ChevronLeft, ChevronRight,
  Save, RotateCcw, Target, Sparkles, Leaf, Bot, Users,
} from "lucide-react";

import {
  API, CATEGORY_COLORS, DEFAULT_EXPENSES, SUMMARY_BAR_COLORS, TREND_LINE_COLORS,
  fmtCAD, fmtCADCompact, monthLabel, monthShort, shiftMonth, currentMonth,
} from "@/lib/budget";
import { Card, StatCard, ProgressBar, NumberInput } from "@/components/primitives";
import UploadCard from "@/components/UploadCard";
import ExpenseEditor from "@/components/ExpenseEditor";
import MonthlyComparison from "@/components/MonthlyComparison";
import MoneyMentor from "@/components/MoneyMentor";
import ParentZone from "@/components/ParentZone";
import KidFamilyPanel from "@/components/KidFamilyPanel";
import SettingsMenu from "@/components/SettingsMenu";
import { useCategories, useBudget, useAllBudgets } from "@/hooks/useBudget";
import { familyAPI } from "@/lib/familyApi";

// Stable chart margins so child charts don't re-render due to new object refs.
const BAR_MARGIN = { top: 16, right: 8, left: 0, bottom: 0 };
const LINE_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const PIE_TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #D8D6D0" };
const LEGEND_STYLE = { fontSize: 12 };
const TICK_12 = { fontSize: 12 };
const TICK_11 = { fontSize: 11 };
const ACTIVE_DOT = { r: 5 };
const LINE_DOT = { r: 3 };

function App() {
  const [month, setMonth] = useState(currentMonth());
  const [savedTick, setSavedTick] = useState(false);
  const [saving, setSaving] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [activeTab, setActiveTab] = useState("budget"); // "budget" | "mentor" | "family"
  const [familySettings, setFamilySettings] = useState(null);

  // Fetch family settings (kid view) for spend-limits + allowance display
  React.useEffect(() => {
    let alive = true;
    familyAPI.getSettingsKid()
      .then((s) => { if (alive) setFamilySettings(s); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [activeTab]);

  const { categories, fixed: fixedCats, variable: variableCats } = useCategories();
  const {
    income, setIncome,
    refundsIncome, setRefundsIncome,
    savingsGoal, setSavingsGoal,
    investments, setInvestments,
    expenses, setExpenses, loading,
  } = useBudget(month);
  const { budgets: allBudgets, refresh: refreshAll } = useAllBudgets();

  // ---------- Derived values ----------
  const totalIncome = useMemo(
    () => (Number(income) || 0) + (Number(refundsIncome) || 0),
    [income, refundsIncome]
  );
  const totalSavingsTarget = useMemo(
    () => (Number(savingsGoal) || 0) + (Number(investments) || 0),
    [savingsGoal, investments]
  );
  const totalExpenses = useMemo(
    () => Object.values(expenses).reduce((sum, v) => sum + (Number(v) || 0), 0),
    [expenses]
  );
  const actualSavings = Math.max(0, totalIncome - totalExpenses);
  const netCashflow = totalIncome - totalExpenses;
  const savingsProgress = totalSavingsTarget > 0 ? Math.min(100, (actualSavings / totalSavingsTarget) * 100) : 0;
  const isOnTrack = actualSavings >= totalSavingsTarget;

  const sumOf = useCallback(
    (cats) => cats.reduce((s, c) => s + (Number(expenses[c]) || 0), 0),
    [expenses]
  );
  const fixedTotal = useMemo(() => sumOf(fixedCats), [sumOf, fixedCats]);
  const variableTotal = useMemo(() => sumOf(variableCats), [sumOf, variableCats]);

  const pieData = useMemo(
    () => (categories || [])
      .map((c) => ({ name: c, value: Number(expenses[c]) || 0, color: CATEGORY_COLORS[c] || "#5F7161" }))
      .filter((d) => d.value > 0),
    [categories, expenses]
  );

  const summaryBars = useMemo(() => [
    { name: "Income", value: totalIncome, fill: SUMMARY_BAR_COLORS.Income },
    { name: "Expenditure", value: totalExpenses, fill: SUMMARY_BAR_COLORS.Expenditure },
    { name: "Savings", value: actualSavings, fill: SUMMARY_BAR_COLORS.Savings },
  ], [totalIncome, totalExpenses, actualSavings]);

  const trendData = useMemo(() => {
    const map = new Map();
    (allBudgets || []).forEach((b) => {
      const exp = Object.values(b.expenses || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      const inc = (Number(b.income) || 0) + (Number(b.refunds_income) || 0);
      map.set(b.month, {
        month: b.month, Income: inc, Expenditure: exp,
        Savings: Math.max(0, inc - exp),
      });
    });
    map.set(month, { month, Income: totalIncome, Expenditure: totalExpenses, Savings: actualSavings });
    return Array.from(map.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6)
      .map((d) => ({ ...d, label: monthShort(d.month) }));
  }, [allBudgets, month, totalIncome, totalExpenses, actualSavings]);

  // ---------- Handlers ----------
  const handleExpChange = useCallback((cat, v) => {
    setExpenses((e) => ({ ...e, [cat]: Number(v) || 0 }));
  }, [setExpenses]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/budget`, {
        month,
        income: Number(income) || 0,
        refunds_income: Number(refundsIncome) || 0,
        savings_goal: Number(savingsGoal) || 0,
        investments: Number(investments) || 0,
        expenses,
      });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1800);
      setUndoSnapshot(null);
      refreshAll();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [month, income, refundsIncome, savingsGoal, investments, expenses, refreshAll]);

  const handleReset = useCallback(() => {
    setIncome(0);
    setRefundsIncome(0);
    setSavingsGoal(1000);
    setInvestments(0);
    setExpenses({ ...DEFAULT_EXPENSES });
  }, [setIncome, setRefundsIncome, setSavingsGoal, setInvestments, setExpenses]);

  const ytdInvestments = useMemo(() => {
    const year = month.split("-")[0];
    const saved = (allBudgets || [])
      .filter((b) => (b.month || "").startsWith(`${year}-`))
      .reduce((s, b) => s + (Number(b.investments) || 0), 0);
    // Include the current (possibly unsaved) month's investments value
    const map = new Map((allBudgets || []).map((b) => [b.month, Number(b.investments) || 0]));
    const savedForThisMonth = map.get(month) || 0;
    const liveInvestments = Number(investments) || 0;
    // Replace saved value for the current month with the live (in-progress) value
    return Math.round((saved - savedForThisMonth + liveInvestments) * 100) / 100;
  }, [allBudgets, month, investments]);
  const handlePrevMonth = useCallback(() => setMonth((m) => shiftMonth(m, -1)), []);
  const handleNextMonth = useCallback(() => setMonth((m) => shiftMonth(m, 1)), []);

  // Apply a review decision from the upload Review modal.
  // decision = { replaceMonth, updateIncome, incomeDelta, refundsDelta, investmentsDelta, expenseTotals }
  // When replaceMonth=true, only the categories touched by the upload are SET.
  // When replaceMonth=false, deltas are ADDED to current values.
  const handleApplyReview = useCallback((decision) => {
    if (!decision) return;
    const { replaceMonth, updateIncome, incomeDelta, refundsDelta, investmentsDelta, expenseTotals,
            hasIncomeData, hasRefundsData, hasInvestmentsData } = decision;
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

    // Snapshot current state so the user can undo this apply with one click.
    setUndoSnapshot({
      income, refundsIncome, investments, expenses: { ...expenses },
      ts: Date.now(),
    });

    if (updateIncome) {
      const mainDelta = Number(incomeDelta) || 0;
      const refDelta = Number(refundsDelta) || 0;
      if (replaceMonth) {
        if (hasIncomeData) setIncome(round2(mainDelta));
        if (hasRefundsData) setRefundsIncome(round2(refDelta));
      } else {
        if (mainDelta > 0) setIncome((prev) => round2((Number(prev) || 0) + mainDelta));
        if (refDelta > 0) setRefundsIncome((prev) => round2((Number(prev) || 0) + refDelta));
      }
    }
    const invDelta = Number(investmentsDelta) || 0;
    if (hasInvestmentsData || invDelta > 0) {
      if (replaceMonth && hasInvestmentsData) {
        setInvestments(round2(invDelta));
      } else if (!replaceMonth && invDelta > 0) {
        setInvestments((prev) => round2((Number(prev) || 0) + invDelta));
      }
    }
    if (expenseTotals && typeof expenseTotals === "object" && Object.keys(expenseTotals).length > 0) {
      setExpenses((prev) => {
        const next = { ...prev };
        Object.entries(expenseTotals).forEach(([cat, val]) => {
          const v = round2(val);
          next[cat] = replaceMonth ? v : round2((Number(prev[cat]) || 0) + v);
        });
        return next;
      });
    }
  }, [income, refundsIncome, investments, expenses, setExpenses, setIncome, setRefundsIncome, setInvestments]);

  const handleUndoApply = useCallback(() => {
    if (!undoSnapshot) return;
    setIncome(undoSnapshot.income);
    setRefundsIncome(undoSnapshot.refundsIncome);
    setInvestments(undoSnapshot.investments);
    setExpenses(undoSnapshot.expenses);
    setUndoSnapshot(null);
  }, [undoSnapshot, setExpenses, setIncome, setRefundsIncome, setInvestments]);

  // ---------- Render ----------
  return (
    <div className="App min-h-screen">
      <AppHeader
        month={month}
        ytdInvestments={ytdInvestments}
        canUndo={!!undoSnapshot}
        onUndo={handleUndoApply}
        onPrev={handlePrevMonth}
        onNext={handleNextMonth}
        onReset={handleReset}
        onSave={handleSave}
        saving={saving}
        savedTick={savedTick}
        activeTab={activeTab}
      />

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8 md:py-12">
        <TabSwitcher activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === "mentor" ? (
          <MoneyMentor />
        ) : activeTab === "family" ? (
          <ParentZone month={month} />
        ) : (
          <>
            <Hero month={month} />

            <StatGrid
              totalIncome={totalIncome}
              income={income}
              refundsIncome={refundsIncome}
              totalExpenses={totalExpenses}
              fixedTotal={fixedTotal}
              variableTotal={variableTotal}
              actualSavings={actualSavings}
              totalSavingsTarget={totalSavingsTarget}
              investments={investments}
              netCashflow={netCashflow}
            />

            <KidFamilyPanel month={month} expenseCategories={categories || []} variableTotal={variableTotal} />

            <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-8">
              <IncomeSavingsCard
                income={income}
                setIncome={setIncome}
                refundsIncome={refundsIncome}
                setRefundsIncome={setRefundsIncome}
                savingsGoal={savingsGoal}
                setSavingsGoal={setSavingsGoal}
                investments={investments}
                setInvestments={setInvestments}
                actualSavings={actualSavings}
                totalSavingsTarget={totalSavingsTarget}
                savingsProgress={savingsProgress}
                isOnTrack={isOnTrack}
              />
              <PieCard pieData={pieData} totalExpenses={totalExpenses} />
              <SummaryBarCard data={summaryBars} />
            </section>

            <UploadCard
              month={month}
              categories={categories}
              currentIncome={totalIncome}
              onApply={handleApplyReview}
            />

            <section className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-8">
              <ExpenseEditor
                income={income}
                totalExpenses={totalExpenses}
                expenses={expenses}
                fixedCats={fixedCats}
                variableCats={variableCats}
                fixedTotal={fixedTotal}
                variableTotal={variableTotal}
                onChange={handleExpChange}
                spendLimits={familySettings?.spend_limits || {}}
              />
              <TrendCard data={trendData} />
            </section>

            <section className="mb-8">
              <MonthlyComparison
                allBudgets={allBudgets}
                liveSnapshot={{ month, expenses }}
                categories={variableCats}
              />
            </section>
          </>
        )}

        <footer className="text-center text-xs text-[#5A5C58] py-6">
          Built with care · Numbers in CAD · {monthLabel(month)}
          {loading && <span className="ml-2 italic">loading…</span>}
        </footer>
      </main>
    </div>
  );
}

// ===================== Tab switcher =====================
const TabSwitcher = ({ activeTab, onChange }) => {
  const tabs = [
    { id: "budget", label: "Budget", icon: Wallet },
    { id: "mentor", label: "Money Mentor", icon: Bot },
    { id: "family", label: "Family", icon: Users },
  ];
  return (
    <div
      data-testid="tab-switcher"
      className="inline-flex items-center gap-1 mb-8 bg-white border border-[#D8D6D0] rounded-full p-1 shadow-[0_1px_3px_rgba(26,28,26,0.04)]"
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = activeTab === t.id;
        return (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => onChange(t.id)}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-sm font-medium transition-all ${
              active
                ? "bg-[#5F7161] text-white shadow-sm"
                : "text-[#5A5C58] hover:bg-[#F4F6F1]"
            }`}
          >
            <Icon size={14} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

// ===================== Header =====================
const AppHeader = ({ month, ytdInvestments, canUndo, onUndo, onPrev, onNext, onReset, onSave, saving, savedTick, activeTab }) => {
  const year = month.split("-")[0];
  const showBudgetControls = activeTab === "budget";
  return (
  <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#D8D6D0]/60">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#5F7161] flex items-center justify-center text-white">
          <Leaf size={18} />
        </div>
        <div>
          <h1 className="font-display text-xl font-semibold leading-tight">Maple Budget</h1>
          <p className="text-xs text-[#5A5C58] -mt-0.5">Monthly plan · CAD</p>
        </div>
      </div>

      {showBudgetControls && (
        <div className="flex items-center gap-1.5 bg-white border border-[#D8D6D0] rounded-full p-1">
          <button
            data-testid="prev-month-btn"
            onClick={onPrev}
            className="w-8 h-8 rounded-full hover:bg-[#E8E6E1] flex items-center justify-center text-[#5F7161] transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <span data-testid="current-month" className="font-display font-medium text-sm px-3 min-w-[160px] text-center">
            {monthLabel(month)}
          </span>
          <button
            data-testid="next-month-btn"
            onClick={onNext}
            className="w-8 h-8 rounded-full hover:bg-[#E8E6E1] flex items-center justify-center text-[#5F7161] transition-colors"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {showBudgetControls && ytdInvestments > 0 && (
          <div
            data-testid="ytd-investments"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3E6B9D14] border border-[#3E6B9D33] text-[#3E6B9D]"
            title={`Sum of Investments across all months in ${year}`}
          >
            <TrendingUp size={13} />
            <span className="text-xs font-medium">
              Invested <span className="font-mono font-semibold">{fmtCAD(ytdInvestments)}</span> in {year}
            </span>
          </div>
        )}
        {showBudgetControls && (
          <button
            data-testid="reset-btn"
            onClick={onReset}
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-[#5F7161] hover:bg-[#E8E6E1]/60 text-sm font-medium transition-colors"
          >
            <RotateCcw size={14} /> Reset
          </button>
        )}
        {showBudgetControls && canUndo && (
          <button
            data-testid="undo-apply-btn"
            onClick={onUndo}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FFF4E5] hover:bg-[#FCEACB] text-[#8A5A1A] text-xs font-medium border border-[#F4D9A7] transition-colors"
            title="Restore values from before the last upload Apply"
          >
            <RotateCcw size={12} /> Undo apply
          </button>
        )}
        {showBudgetControls && (
          <button
            data-testid="save-btn"
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium transition-colors"
          >
            {savedTick ? <Sparkles size={14} /> : <Save size={14} />}
            {savedTick ? "Saved" : saving ? "Saving…" : "Save Budget"}
          </button>
        )}
        <SettingsMenu />
      </div>
    </div>
  </header>
  );
};

const Hero = ({ month }) => (
  <div className="mb-8">
    <p className="text-xs uppercase tracking-[0.25em] text-[#5A5C58] font-medium">Your Plan · {monthLabel(month)}</p>
    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight mt-2 max-w-3xl">
      A calmer way to see where your loonies go.
    </h2>
  </div>
);

// ===================== Stat Grid =====================
const StatGrid = ({ totalIncome, income, refundsIncome, totalExpenses, fixedTotal, variableTotal, actualSavings, totalSavingsTarget, investments, netCashflow }) => (
  <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
    <StatCard icon={TrendingUp}   label="Income"        value={fmtCAD(totalIncome)}
              sub={refundsIncome > 0 ? `Salary ${fmtCAD(income)} · Refunds ${fmtCAD(refundsIncome)}` : "Monthly inflow"}
              accent="#5F7161" testId="card-income"        delay={0} />
    <StatCard icon={TrendingDown} label="Expenditure"   value={fmtCAD(totalExpenses)}
              sub={`Fixed ${fmtCAD(fixedTotal)} · Variable ${fmtCAD(variableTotal)}`}
              accent="#E27D60" testId="card-expenditure"   delay={80} />
    <StatCard icon={PiggyBank}    label="Savings"       value={fmtCAD(actualSavings)}
              sub={investments > 0 ? `Goal ${fmtCAD(totalSavingsTarget)} (incl. ${fmtCAD(investments)} invest.)` : `Goal ${fmtCAD(totalSavingsTarget)}`}
              accent="#6B8E23" testId="card-savings"       delay={160} />
    <StatCard icon={Wallet}       label="Net Cashflow"  value={fmtCAD(netCashflow)}    sub={netCashflow >= 0 ? "Surplus" : "Deficit"}
              accent={netCashflow >= 0 ? "#5F7161" : "#E27D60"} testId="card-cashflow"  delay={240} />
  </section>
);

// ===================== Income & Savings card =====================
const SAVINGS_COLORS = { savings: "#6B8E23", investments: "#5F7161" };

const SavingsDonut = ({ savingsGoal, investments }) => {
  const data = useMemo(() => {
    const s = Number(savingsGoal) || 0;
    const i = Number(investments) || 0;
    if (s + i === 0) return [];
    return [
      { name: "Savings", value: s, fill: SAVINGS_COLORS.savings },
      { name: "Investments", value: i, fill: SAVINGS_COLORS.investments },
    ].filter((d) => d.value > 0);
  }, [savingsGoal, investments]);

  const total = (Number(savingsGoal) || 0) + (Number(investments) || 0);
  if (data.length === 0) return null;

  const sPct = total > 0 ? Math.round(((Number(savingsGoal) || 0) / total) * 100) : 0;
  const iPct = 100 - sPct;

  return (
    <div data-testid="savings-vs-investments-donut" className="flex items-center gap-4">
      <div className="w-[88px] h-[88px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={26} outerRadius={42} paddingAngle={2} stroke="#FFFFFF" strokeWidth={2}>
              {data.map((d) => <Cell key={d.name} fill={d.fill} />)}
            </Pie>
            <RTooltip formatter={(v) => fmtCAD(v)} contentStyle={{ borderRadius: 10, border: "1px solid #D8D6D0", fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: SAVINGS_COLORS.savings }} />
          <span className="text-[#5A5C58]">Savings</span>
          <span className="font-mono text-[#1A1C1A] ml-1">{fmtCAD(savingsGoal)}</span>
          <span className="text-[#8A8C88]">· {sPct}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: SAVINGS_COLORS.investments }} />
          <span className="text-[#5A5C58]">Investments</span>
          <span className="font-mono text-[#1A1C1A] ml-1">{fmtCAD(investments)}</span>
          <span className="text-[#8A8C88]">· {iPct}%</span>
        </div>
      </div>
    </div>
  );
};

const IncomeSavingsCard = ({
  income, setIncome,
  refundsIncome, setRefundsIncome,
  savingsGoal, setSavingsGoal,
  investments, setInvestments,
  actualSavings, totalSavingsTarget, savingsProgress, isOnTrack,
}) => (
  <Card className="md:col-span-4 p-6" testId="card-income-input" delay={0}>
    <h3 className="font-display text-lg font-semibold mb-1">Income &amp; Savings</h3>
    <p className="text-sm text-[#5A5C58] mb-5">All amounts in Canadian dollars.</p>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Income</label>
        <NumberInput value={income} onChange={setIncome} testId="input-income" />
      </div>
      <div>
        <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Refunds</label>
        <NumberInput value={refundsIncome} onChange={setRefundsIncome} testId="input-refunds-income" />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3 mt-4">
      <div>
        <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Savings Goal</label>
        <NumberInput value={savingsGoal} onChange={setSavingsGoal} testId="input-savings-goal" />
      </div>
      <div>
        <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Investments</label>
        <NumberInput value={investments} onChange={setInvestments} testId="input-investments" />
      </div>
    </div>

    <div className="mt-6 p-4 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA]">
      <div className="flex items-center gap-2 mb-2">
        <Target size={14} className="text-[#5F7161]" />
        <p className="text-xs uppercase tracking-[0.18em] font-medium text-[#5A5C58]">Savings Goal Tracker</p>
      </div>
      <div className="flex items-baseline justify-between mb-2">
        <span data-testid="savings-actual" className="font-display text-2xl font-semibold text-[#1A1C1A]">
          {fmtCAD(actualSavings)}
        </span>
        <span className="text-sm text-[#5A5C58] font-mono">/ {fmtCAD(totalSavingsTarget)}</span>
      </div>
      <ProgressBar value={actualSavings} max={totalSavingsTarget} color="#6B8E23" />
      <p className={`text-xs mt-2 font-medium ${isOnTrack ? "text-[#5F7161]" : "text-[#B8593E]"}`}>
        {totalSavingsTarget <= 0
          ? "Set a savings goal or investments target to start tracking."
          : isOnTrack
          ? `Nice — you're ${Math.round(savingsProgress)}% of goal. Keep going!`
          : `${Math.round(savingsProgress)}% of goal · ${fmtCAD(Math.max(0, totalSavingsTarget - actualSavings))} to go`}
      </p>

      {(Number(savingsGoal) + Number(investments)) > 0 && (
        <div className="mt-4 pt-4 border-t border-[#E0E5DA]">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-[#5A5C58] mb-2">Target split</p>
          <SavingsDonut savingsGoal={savingsGoal} investments={investments} />
        </div>
      )}
    </div>
  </Card>
);

// ===================== Pie chart card =====================
const PieCard = ({ pieData, totalExpenses }) => (
  <Card className="md:col-span-4 p-6" testId="card-pie" delay={80}>
    <div className="flex items-baseline justify-between mb-1">
      <h3 className="font-display text-lg font-semibold">Expense Breakdown</h3>
      <span className="text-xs text-[#5A5C58] font-mono">{fmtCAD(totalExpenses)}</span>
    </div>
    <p className="text-sm text-[#5A5C58] mb-2">Where your spending goes this month.</p>
    <div className="h-[260px]">
      {pieData.length === 0 ? (
        <div className="h-full flex items-center justify-center text-sm text-[#5A5C58]">
          Add expenses to see the breakdown
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData} dataKey="value" nameKey="name"
              innerRadius={55} outerRadius={95} paddingAngle={2}
              stroke="#FFFFFF" strokeWidth={2}
            >
              {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <RTooltip formatter={fmtCAD} contentStyle={PIE_TOOLTIP_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
    <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2">
      {pieData.map((d) => (
        <div key={d.name} className="flex items-center gap-1.5 text-xs">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
          <span className="text-[#1A1C1A]">{d.name}</span>
        </div>
      ))}
    </div>
  </Card>
);

// ===================== Summary bar card =====================
const SummaryBarCard = ({ data }) => (
  <Card className="md:col-span-4 p-6" testId="card-bar" delay={160}>
    <h3 className="font-display text-lg font-semibold mb-1">Income vs Spend vs Save</h3>
    <p className="text-sm text-[#5A5C58] mb-2">A quick at-a-glance comparison.</p>
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={BAR_MARGIN}>
          <CartesianGrid stroke="#EFEDE7" vertical={false} />
          <XAxis dataKey="name" stroke="#5A5C58" tick={TICK_12} axisLine={false} tickLine={false} />
          <YAxis stroke="#5A5C58" tick={TICK_12} axisLine={false} tickLine={false} tickFormatter={fmtCADCompact} />
          <RTooltip formatter={fmtCAD} />
          <Bar dataKey="value" radius={[10, 10, 0, 0]}>
            {data.map((d) => <Cell key={d.name} fill={d.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// ===================== Trend chart card =====================
const TrendCard = ({ data }) => (
  <Card className="md:col-span-5 p-6 sm:p-8" testId="card-trend" delay={80}>
    <h3 className="font-display text-xl font-semibold">Monthly Trend</h3>
    <p className="text-sm text-[#5A5C58] mb-4">Last 6 months of income, spend &amp; savings.</p>
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={LINE_MARGIN}>
          <CartesianGrid stroke="#EFEDE7" vertical={false} />
          <XAxis dataKey="label" stroke="#5A5C58" tick={TICK_11} axisLine={false} tickLine={false} />
          <YAxis stroke="#5A5C58" tick={TICK_11} axisLine={false} tickLine={false} tickFormatter={fmtCADCompact} />
          <RTooltip formatter={fmtCAD} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          <Line type="monotone" dataKey="Income"      stroke={TREND_LINE_COLORS.Income}      strokeWidth={2.5} dot={LINE_DOT} activeDot={ACTIVE_DOT} />
          <Line type="monotone" dataKey="Expenditure" stroke={TREND_LINE_COLORS.Expenditure} strokeWidth={2.5} dot={LINE_DOT} activeDot={ACTIVE_DOT} />
          <Line type="monotone" dataKey="Savings"     stroke={TREND_LINE_COLORS.Savings}     strokeWidth={2.5} dot={LINE_DOT} activeDot={ACTIVE_DOT} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

export default App;
